'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { memo } from 'react'
import { useForm, useWatch } from 'react-hook-form'

import { useUploadUrl } from '@/entities/storage'
import { ImageUpload } from '@/shared/lib'
import { Notice } from '@/shared/ui'
import { Button, Dialog, Form, Image, Separator } from '@/shared/ui'

import { useValidatePhoneNumber } from '../api/validate-phone-number'
import PassportSampleImage from '../asset/passport-sample.png'
import {
  passportVerificationFormDefaultValues,
  TPassportVerificationForm,
  ZPassportVerificationForm,
} from '../model/passport-verification-form'

export const PASSPORT_VERIFICATION_DIALOG = 'PASSPORT_VERIFICATION'

type Props = {
  isOpen: boolean
  onClose: () => void
  onSuccess: (formValues: TPassportVerificationForm) => void
  isVerified?: boolean
  defaultValues?: Partial<TPassportVerificationForm>
}

export const PassportDialog = ({
  isOpen,
  onClose,
  onSuccess,
  isVerified = false,
  defaultValues,
}: Props) => {
  const { generateUploadUrl } = useUploadUrl()
  const { validatePhoneNumberAsync } = useValidatePhoneNumber()

  const form = useForm<TPassportVerificationForm>({
    defaultValues: defaultValues || passportVerificationFormDefaultValues,
    resolver: zodResolver(ZPassportVerificationForm),
    reValidateMode: 'onSubmit',
  })

  const { control, setValue, handleSubmit } = form

  const passportImageUrl = useWatch({
    control: control,
    name: 'passportImageUrl',
  })

  const submitForm = async ({
    contactNumber,
    passportImageUrl,
  }: TPassportVerificationForm) => {
    const initialContactNumber = defaultValues?.contactNumber

    if (initialContactNumber !== contactNumber) {
      const isValidPhoneNumber = await validatePhoneNumberAsync(contactNumber)

      if (!isValidPhoneNumber) {
        return
      }
    }

    if (passportImageUrl) {
      onSuccess({ contactNumber, passportImageUrl })
    }
  }

  const handleUploadpassportImage = async ({ file }: { file: File }) => {
    const url = await generateUploadUrl(file, {
      fileName: file.name,
      fileSize: file.size,
      prefix: 'registid',
    })

    if (!url) return

    setValue('passportImageUrl', url)
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Content className="h-[760px] gap-0 p-0">
        <Dialog.Header className="p-6">
          <Dialog.Title>여권 본인인증</Dialog.Title>
          <Dialog.Description className="hidden">
            여권 사진을 등록해 주세요
          </Dialog.Description>
        </Dialog.Header>

        <div className="scrollbar thick flex grow flex-col overflow-auto p-6 pt-0">
          <h3 className="typo-body2 font-semibold">
            휴대폰 본인인증이 어려운 경우{' '}
            <strong className="font-semibold text-purple-500">여권 사진</strong>
            을 등록해 주세요
          </h3>
          <p className="typo-label2 mt-1 text-gray-500">
            관리자의 승인 후 본인인증 처리가 완료됩니다.
            <br />
            승인 후 여권 사본은 영구적으로 삭제됩니다.
          </p>
          <Form.Root form={form} className="mt-8 space-y-6">
            <Form.Field name="contactNumber">
              <Form.Label bold>전화번호</Form.Label>
              <Form.TextField
                type="tel"
                placeholder="국가 번호를 포함하여 입력해 주세요"
                fullWidth
              />
              <Form.Message />
            </Form.Field>
            {passportImageUrl && <PassportImage src={passportImageUrl} />}
          </Form.Root>

          <Separator className="my-6" />

          <Notice.Title>주의사항</Notice.Title>
          <Notice.List
            gap={false}
            className="typo-label1 text-black *:before:bg-black"
          >
            <Notice.Item>
              본인인증 신청일 기준으로{' '}
              <strong className="font-normal text-purple-500">
                여권 만료일이 유효
              </strong>
              해야 합니다.
            </Notice.Item>
            <Notice.Item>
              <strong className="font-normal text-purple-500">
                여권번호를 반드시 가리고{' '}
              </strong>
              사진을 찍어주세요.
            </Notice.Item>
            <Notice.Item>
              여권의 내용을 확인할 수 있도록 정면 사진을 촬영해 주세요.
            </Notice.Item>
          </Notice.List>

          <SampleImage />
        </div>

        <Dialog.Footer className="flex-row gap-2.5 p-6">
          {(() => {
            if (!passportImageUrl && !isVerified) {
              return (
                <ImageUpload
                  onChange={handleUploadpassportImage}
                  className="flex-1"
                >
                  <Button size="large" fullWidth>
                    여권 사진 등록하기
                  </Button>
                </ImageUpload>
              )
            }

            return (
              <>
                <ImageUpload
                  onChange={handleUploadpassportImage}
                  className="flex-1"
                >
                  <Button color="secondary" size="large" fullWidth>
                    수정
                  </Button>
                </ImageUpload>
                <Button
                  size="large"
                  fullWidth
                  className="flex-1"
                  onClick={handleSubmit(submitForm)}
                >
                  등록 완료
                </Button>
              </>
            )
          })()}
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  )
}

const PassportImage = memo(({ src }: { src: string }) => (
  <Form.Field name="passportImage">
    <>
      <Form.Label bold>여권</Form.Label>
      <Image
        src={src}
        alt="여권"
        width={335}
        height={219}
        external={true}
        className="rounded-sm border border-gray-100"
      />
    </>
  </Form.Field>
))

PassportImage.displayName = 'PassportImage'

const SampleImage = memo(() => (
  <Image
    src={PassportSampleImage}
    alt="여권"
    width={335}
    height={219}
    className="mt-4 overflow-visible"
    external={false}
  />
))

SampleImage.displayName = 'SampleImage'
